// ====================== FULL UPDATED WORKING FILE ======================
// src/components/EmployeeLogDetail.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuthStore } from "../store/useAuthStore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
const MOBILE_BREAKPOINT = 768;
const norm = (v) => String(v || "").toLowerCase();

/* Convert decimal hours → HH:MM */
function formatHours(decimal) {
  if (!decimal && decimal !== 0) return "00:00";
  let hours = Math.floor(decimal);
  let minutes = Math.round((decimal - hours) * 60);
  if (minutes < 10) minutes = "0" + minutes;
  return `${hours}:${minutes}`;
}

const EmployeeLogDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { authUser } = useAuthStore();

  const [logs, setLogs] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= MOBILE_BREAKPOINT
      : false,
  );

  // Attendance States
  const [attendanceData, setAttendanceData] = useState([]);
  const [attLoading, setAttLoading] = useState(false);

  const getMonthLabel = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });

  // Handle Resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Attendance vs Actual Difference
  function calcAttendanceDiff(attNet, actual) {
    const diff = (actual || 0) - (attNet || 0); // 🔥 Correct Formula
    return {
      value: diff,
      color: diff < 0 ? "red" : "green",
      text: `${diff < 0 ? "-" : "+"}${formatHours(Math.abs(diff))}`,
    };
  }

  // =====================================================
  //               FETCH LOGS + EMPLOYEE NAME
  // =====================================================
  // =====================================================
  // FETCH LOGS + EMPLOYEE NAME
  // =====================================================
  useEffect(() => {
    if (!authUser || !userId) return;

    let active = true;

    const loadLogs = async () => {
      try {
        setLoading(true);

        const role = norm(authUser.role);

        if (role === "client") {
          navigate("/messenger", { replace: true });
          return;
        }

        const myUserId = String(authUser?._id || authUser?.id || "");

        const targetUserId = String(userId);

        const isOwnLogs = myUserId === targetUserId;

        let res;

        // Own logs never need viewAll permission.
        if (isOwnLogs) {
          res = await api.get("/logs/my");
        } else {
          // Backend decides whether this user has
          // employeeLog.viewAll permission.
          res = await api.get(`/logs/user/${targetUserId}`);
        }

        if (!active) return;

        const data = Array.isArray(res.data) ? res.data : [];

        setLogs(data);

        if (data.length > 0) {
          setEmployeeName(
            data[0]?.user?.name || data[0]?.userName || "Employee",
          );
        } else {
          setEmployeeName("Employee");
        }

        const months = Array.from(
          new Set(
            data
              .filter((item) => item?.date)
              .map((item) => getMonthLabel(item.date)),
          ),
        );

        if (months.length > 0) {
          const sortKey = (monthLabel) => {
            const [month, year] = monthLabel.split(" ");

            return new Date(`${month} 01, ${year}`).getTime();
          };

          months.sort((a, b) => sortKey(b) - sortKey(a));

          setSelectedMonth(months[0]);
        } else {
          setSelectedMonth("");
        }
      } catch (error) {
        if (!active) return;

        console.error("Failed to fetch employee logs:", error);

        setLogs([]);
        setSelectedMonth("");

        const status = error?.response?.status;

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch employee logs.";

        if (status === 403) {
          alert(
            message ||
              "You do not have permission to view this employee's logs.",
          );

          navigate(-1);
          return;
        }

        alert(message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadLogs();

    return () => {
      active = false;
    };
  }, [authUser, userId, navigate]);

  // =====================================================
  //                 FETCH ATTENDANCE
  // =====================================================
  useEffect(() => {
    if (!selectedMonth || !employeeName) return;

    const [mon, yr] = selectedMonth.split(" ");
    const monthNum = new Date(`${mon} 1, ${yr}`).getMonth() + 1;

    setAttLoading(true);

    api
      .get(`/attendance/daily-monthly-report?month=${monthNum}&year=${yr}`)
      .then((res) => {
        const rows = res.data.dailyReport || [];

        const fullName = employeeName.trim().toLowerCase();
        const firstName = fullName.split(" ")[0];

        const clean = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();

        const filtered = rows.filter((r) => {
          const attName = clean(r.EmployeeName);
          return (
            attName === firstName ||
            fullName.includes(attName) ||
            attName.includes(firstName) ||
            attName.replace(/\s/g, "") === firstName.replace(/\s/g, "")
          );
        });

        setAttendanceData(filtered);
      })
      .catch(() => {})
      .finally(() => setAttLoading(false));
  }, [selectedMonth, employeeName]);

  // Unique Months
  const uniqueMonths = useMemo(() => {
    const months = Array.from(new Set(logs.map((l) => getMonthLabel(l.date))));
    const sortKey = (m) => {
      const [mon, yr] = m.split(" ");
      return new Date(`${mon} 01, ${yr}`).getTime();
    };
    return months.sort((a, b) => sortKey(b) - sortKey(a));
  }, [logs]);

  // Project List
  const projectsForSelectedMonth = useMemo(() => {
    if (!selectedMonth) return [];
    const set = new Set(
      logs
        .filter((l) => getMonthLabel(l.date) === selectedMonth)
        .map((l) => l.projectName)
        .filter(Boolean),
    );
    return Array.from(set).sort();
  }, [logs, selectedMonth]);

  useEffect(() => {
    if (
      selectedProject &&
      !projectsForSelectedMonth.includes(selectedProject)
    ) {
      setSelectedProject("");
    }
  }, [selectedMonth, projectsForSelectedMonth, selectedProject]);

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const logDate = new Date(log.date);

      const matchMonth =
        !selectedMonth || getMonthLabel(log.date) === selectedMonth;

      const matchProject =
        !selectedProject || log.projectName === selectedProject;

      const matchFrom =
        !fromDate || logDate >= new Date(fromDate + "T00:00:00");

      const matchTo = !toDate || logDate <= new Date(toDate + "T23:59:59");

      return matchMonth && matchProject && matchFrom && matchTo;
    });
  }, [logs, selectedMonth, selectedProject, fromDate, toDate]);

  const totalEstimated = filteredLogs.reduce(
    (t, x) => t + (parseFloat(x.estimatedHours) || 0),
    0,
  );
  const totalActual = filteredLogs.reduce(
    (t, x) => t + (parseFloat(x.actualHours) || 0),
    0,
  );
  const totalDiff = totalEstimated - totalActual;

  const groupedByCategory = useMemo(() => {
    const groups = {};
    filteredLogs.forEach((log) => {
      let cat = log.projectCategory || "General";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(log);
    });
    return groups;
  }, [filteredLogs]);

  // Attendance totals
  const totalAttendanceNet = attendanceData.reduce(
    (t, r) => t + (r.NetHours || 0),
    0,
  );

  const diffResult = calcAttendanceDiff(totalAttendanceNet, totalActual);

  // ================================
  //     RENDER
  // ================================
  if (loading) return <div style={{ padding: 24 }}>Loading logs…</div>;
  const downloadExcel = () => {
    if (filteredLogs.length === 0) {
      alert("No data to export");
      return;
    }

    const rows = filteredLogs.map((l) => {
      const est = parseFloat(l.estimatedHours || 0);
      const act = parseFloat(l.actualHours || 0);

      return {
        Project: l.projectName,
        Category: l.projectCategory || "General",
        WorkType: l.workType,
        DrawingDocument: l.drawingDocument,
        Remark: l.remark,
        Date: new Date(l.date).toLocaleDateString("en-GB"),
        StartTime: l.startTime,
        EndTime: l.endTime,
        EstimatedHours: est.toFixed(2),
        ActualHours: act.toFixed(2),
        Difference: (est - act).toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

    const fileName = `${employeeName}_${selectedMonth || "All"}_Logs.xlsx`;

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <div
      style={{
        padding: isMobile ? 16 : 40,
        background: "#f7f9fc",
        height: "100%",
        overflowY: "auto",
      }}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "#343a76",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
          marginBottom: 20,
        }}
      >
        ← Back
      </button>

      {/* TITLE */}
      <h2 style={{ textAlign: "center", color: "#343a76", marginBottom: 25 }}>
        📋 {employeeName}'s Monthly Log Summary
      </h2>

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          gap: 20,
          alignItems: "flex-end", // 🔥 sab same baseline
          justifyContent: "center",
          flexWrap: "nowrap", // 🔥 line break band
        }}
      >
        <div>
          <label style={{ fontWeight: "bold" }}>Select Month: </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={dropdownStyle}
          >
            <option value="">Select Month</option>
            {uniqueMonths.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontWeight: "bold" }}>Filter by Project: </label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            disabled={!selectedMonth}
            style={dropdownStyle}
          >
            <option value="">
              {selectedMonth ? "All Projects" : "Select Month First"}
            </option>
            {projectsForSelectedMonth.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontWeight: "bold" }}>From Date:</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={dropdownStyle}
          />
        </div>

        <div>
          <label style={{ fontWeight: "bold" }}>To Date:</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={dropdownStyle}
          />
        </div>
      </div>
      <div style={{ textAlign: "center", margin: "20px 0" }}>
        <button
          onClick={downloadExcel}
          style={{
            background: "#198754",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          ⬇ Download Excel
        </button>
      </div>

      {/* =============================
            LOG TABLE
      ============================== */}
      {selectedMonth && (
        <>
          <h4 style={{ textAlign: "center", marginTop: 20 }}>
            Logs for {selectedMonth}
          </h4>

          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead style={{ background: "#343a76", color: "#fff" }}>
                <tr>
                  <th style={styles.thLeft}>Project</th>
                  <th style={styles.th}>Drawing</th>
                  <th style={styles.th}>Drawing & Document</th>
                  <th style={styles.th}>Remark</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Start</th>
                  <th style={styles.th}>End</th>
                  <th style={styles.th}>Estimated</th>
                  <th style={styles.th}>Actual</th>
                  <th style={styles.th}>Diff</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(groupedByCategory).map(([cat, list]) => (
                  <React.Fragment key={cat}>
                    <tr style={{ background: "#e0e0e0" }}>
                      <td colSpan="10" style={styles.categoryRow}>
                        {cat.toUpperCase()}
                      </td>
                    </tr>

                    {list.map((log, idx) => {
                      const est = parseFloat(log.estimatedHours || 0);
                      const act = parseFloat(log.actualHours || 0);
                      const diff = est - act;

                      return (
                        <tr
                          key={log._id}
                          style={{
                            background: idx % 2 === 0 ? "#f9f9f9" : "#fff",
                          }}
                        >
                          <td style={styles.tdLeft}>{log.projectName}</td>
                          <td style={styles.td}>{log.workType}</td>
                          <td style={styles.td}>{log.drawingDocument}</td>
                          <td style={styles.td}>{log.remark}</td>
                          <td style={styles.td}>
                            {new Date(log.date).toLocaleDateString("en-GB")}
                          </td>
                          <td style={styles.td}>{log.startTime}</td>
                          <td style={styles.td}>{log.endTime}</td>
                          <td style={styles.td}>{est.toFixed(2)}</td>
                          <td style={styles.td}>{act.toFixed(2)}</td>
                          <td
                            style={{
                              ...styles.td,
                              fontWeight: "bold",
                              color: diff < 0 ? "red" : "green",
                            }}
                          >
                            {diff.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}

                {filteredLogs.length > 0 && (
                  <tr style={{ background: "#eef1f8", fontWeight: "bold" }}>
                    <td colSpan="7" style={{ textAlign: "right", padding: 12 }}>
                      Total:
                    </td>
                    <td style={styles.td}>{totalEstimated.toFixed(2)}</td>
                    <td style={styles.td}>{totalActual.toFixed(2)}</td>
                    <td
                      style={{
                        ...styles.td,
                        color: totalDiff < 0 ? "red" : "green",
                      }}
                    >
                      {totalDiff.toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* =============================
                 ATTENDANCE SECTION
          ============================== */}
          <div
            style={{
              marginTop: 50,
              padding: 20,
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <h3 style={{ color: "#343a76", marginBottom: 10 }}>
              📅 Attendance Summary – {employeeName} ({selectedMonth})
            </h3>

            {attLoading ? (
              <div>Loading attendance…</div>
            ) : attendanceData.length === 0 ? (
              <div style={{ padding: 12, color: "#777" }}>
                No attendance found for this month.
              </div>
            ) : (
              <>
                <table
                  border="1"
                  cellPadding="8"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: 10,
                    fontSize: 14,
                  }}
                >
                  <thead style={{ background: "#343a76", color: "#fff" }}>
                    <tr>
                      <th>Date</th>
                      <th>InTime1</th>
                      <th>OutTime1</th>
                      <th>InTime2</th>
                      <th>OutTime2</th>
                      <th>Span</th>
                      <th>Break</th>
                      <th>Net</th>
                    </tr>
                  </thead>

                  <tbody>
                    {attendanceData.map((r, i) => (
                      <tr
                        key={i}
                        style={{
                          background: i % 2 === 0 ? "#f9f9f9" : "#fff",
                        }}
                      >
                        <td>{r.WorkDate?.substring(0, 10)}</td>
                        <td>{r.InTime1 || "-"}</td>
                        <td>{r.OutTime1 || "-"}</td>
                        <td>{r.InTime2 || "-"}</td>
                        <td>{r.OutTime2 || "-"}</td>
                        <td>{formatHours(r.TotalSpanHours)}</td>
                        <td>{formatHours(r.BreakHours)}</td>
                        <td>{formatHours(r.NetHours)}</td>
                      </tr>
                    ))}

                    <tr style={{ background: "#e3ffe3", fontWeight: "bold" }}>
                      <td colSpan="5" style={{ textAlign: "right" }}>
                        Monthly Total →
                      </td>
                      <td>
                        {formatHours(
                          attendanceData.reduce(
                            (t, r) => t + (r.TotalSpanHours || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td>
                        {formatHours(
                          attendanceData.reduce(
                            (t, r) => t + (r.BreakHours || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td>
                        {formatHours(
                          attendanceData.reduce(
                            (t, r) => t + (r.NetHours || 0),
                            0,
                          ),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* ========= FINAL SUMMARY ROWS ========= */}
                <table
                  style={{
                    width: "100%",
                    marginTop: 30,
                    borderCollapse: "collapse",
                    fontSize: 36, // BIGGER FONT
                    background: "#eef1ff",
                    borderRadius: 14,
                    fontWeight: 900, // SUPER BOLD
                  }}
                >
                  <tbody>
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "left",
                          padding: "18px 20px",
                          color: "#000",
                          fontWeight: 900,
                        }}
                      >
                        Monthy Hours →
                      </td>
                      <td
                        style={{
                          padding: "18px 20px",
                          textAlign: "right",
                          fontWeight: 900,
                        }}
                      >
                        {formatHours(totalAttendanceNet)}
                      </td>
                    </tr>

                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "left",
                          padding: "18px 20px",
                          color: "#000",
                          fontWeight: 900,
                        }}
                      >
                        Working Hours →
                      </td>
                      <td
                        style={{
                          padding: "18px 20px",
                          textAlign: "right",
                          fontWeight: 900,
                        }}
                      >
                        {formatHours(totalActual)}
                      </td>
                    </tr>

                    <tr style={{ color: diffResult.color }}>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "left",
                          padding: "18px 20px",
                          fontWeight: 900,
                        }}
                      >
                        Difference →
                      </td>
                      <td
                        style={{
                          padding: "18px 20px",
                          textAlign: "right",
                          fontWeight: 900,
                        }}
                      >
                        {diffResult.text}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Styles ----------
const dropdownStyle = {
  padding: "8px 14px",
  borderRadius: 6,
  border: "1px solid #ccc",
  background: "#fff",
  fontSize: 15,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
};

const styles = {
  th: {
    padding: 12,
    fontWeight: 600,
    borderBottom: "1px solid #ddd",
    textAlign: "center",
  },
  thLeft: {
    padding: 12,
    fontWeight: 600,
    borderBottom: "1px solid #ddd",
    textAlign: "left",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid #eee",
    textAlign: "center",
  },
  tdLeft: {
    padding: 10,
    borderBottom: "1px solid #eee",
    textAlign: "left",
  },
  categoryRow: {
    padding: 12,
    fontWeight: "bold",
    fontSize: 15,
  },
};

export default EmployeeLogDetail;
